<template>
  <div class="flex flex-col gap-4">
    <!-- The recency of the validating sale only makes sense for a program that has some. -->
    <div v-if="hasValidation" class="flex flex-wrap items-center gap-2">
      <span class="text-sm text-gray-dark">{{ meta.labels?.excluded || 'Dernière vente validante' }}</span>
      <button
        v-for="option in RECENCY_OPTIONS"
        :key="option.months"
        type="button"
        class="h-7 px-3 rounded-full text-xs font-medium transition-colors duration-200"
        :class="state.recency === option.months ? 'bg-primary text-white' : 'bg-gray-light text-gray-dark hover:bg-apodisPink'"
        @click="state.recency = option.months"
      >
        {{ option.label }}
      </button>
    </div>

    <TooltipProvider :delay-duration="150">
      <div class="rounded-lg border border-gray-light overflow-hidden">
        <div class="relative grid grid-cols-[18rem_1fr] h-10 bg-primary-lightest border-b border-gray-light">
          <!-- The page loading, a step per source received, faded out once complete. -->
          <div
            :key="state.page"
            class="absolute left-0 -bottom-px h-0.5 bg-primary"
            :class="pageProgress < 1 ? 'opacity-100' : 'opacity-0'"
            :style="{width: `${Math.max(pageProgress, 0.05) * 100}%`, transition: 'width 400ms ease-out, opacity 300ms ease 400ms'}"
            role="progressbar"
            aria-label="Chargement de l'historique des patients"
            :aria-valuenow="Math.round(pageProgress * 100)"
          />
          <div class="flex items-center justify-between px-3 text-sm font-semibold text-gray-dark">
            Patient
            <div class="flex items-center gap-1">
              <Button variant="ghost" size="icon" class="size-6" :disabled="!canGoBack" aria-label="Période précédente" @click="state.offset -= PAN_MONTHS">
                <ChevronLeft class="size-4" />
              </Button>
              <Button variant="outline" size="sm" class="h-6 px-2 text-xs border-solid" :disabled="state.offset === 0" @click="state.offset = 0">
                aujourd'hui
              </Button>
              <Button variant="ghost" size="icon" class="size-6" :disabled="state.offset >= 0" aria-label="Période suivante" @click="state.offset += PAN_MONTHS">
                <ChevronRight class="size-4" />
              </Button>
            </div>
          </div>
          <div class="relative mx-4">
            <span
              v-for="month in months"
              :key="month.label"
              class="absolute top-1/2 -translate-y-1/2 pl-1 text-xs text-gray-dark"
              :style="{left: `${month.position * 100}%`}"
            >
              {{ month.label }}
            </span>
            <span
              v-if="todayPosition >= 0 && todayPosition <= 1"
              class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 px-2 rounded-full bg-red-light text-white text-tiny font-semibold leading-4"
              :style="{left: `${todayPosition * 100}%`}"
            >
              aujourd'hui
            </span>
          </div>
        </div>

        <p v-if="!pageConsumers.length" class="py-10 text-center text-sm text-gray-dark">
          Aucun patient ne correspond à ces filtres.
        </p>
        <div
          v-for="consumer in pageConsumers"
          :key="consumer.key"
          class="grid grid-cols-[18rem_1fr] h-12 border-b border-gray-light last:border-b-0 hover:bg-primary-lightest/60"
        >
          <FollowUpPatientCell
            :consumer="consumer"
            :groups="groups"
            :meta="meta"
            :has-validation="hasValidation"
            @open="emit('open', consumer)"
            @conversation="showConversation(consumer)"
          />
          <div class="mx-4">
            <FollowUpTimelineTrack
              :events="state.events[consumer.key]"
              :from="axis.from"
              :to="axis.to"
              :ticks="months.map(month => month.position)"
              :today="todayPosition"
            />
          </div>
        </div>
      </div>
    </TooltipProvider>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
      <span
        v-for="(style, kind) in FOLLOW_UP_EVENT_STYLES"
        :key="kind"
        class="flex items-center gap-1.5 text-xs text-gray-dark"
      >
        <span class="flex items-center justify-center size-5 rounded-full border-[1.5px]" :class="style.classes">
          <component :is="style.icon" class="size-3" />
        </span>
        {{ FOLLOW_UP_EVENT_LABELS[kind] }}
      </span>
      <span class="flex items-center gap-1.5 text-xs text-gray-dark">
        <span class="flex items-center justify-center size-5 rounded-full border-[1.5px]" :class="FOLLOW_UP_PLANNED_CLASSES">
          <MessageSquareText class="size-3" />
        </span>
        À venir (planifié)
      </span>
      <div class="flex items-center gap-2 ml-auto text-sm text-gray-dark">
        <span v-if="sortedConsumers.length">
          {{ pageStart + 1 }}–{{ pageStart + pageConsumers.length }} sur {{ sortedConsumers.length }} patients
        </span>
        <Button variant="ghost" size="icon" class="size-7" :disabled="state.page <= 1" aria-label="Page précédente" @click="state.page--">
          <ChevronLeft class="size-4" />
        </Button>
        <span class="font-semibold">{{ state.page }} / {{ pageCount }}</span>
        <Button variant="ghost" size="icon" class="size-7" :disabled="state.page >= pageCount" aria-label="Page suivante" @click="state.page++">
          <ChevronRight class="size-4" />
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, markRaw, reactive, watch} from "vue";
import {useRouter} from "vue-router";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {ChevronLeft, ChevronRight, MessageSquareText} from "@lucide/vue";
import {programApi} from "@/container";
import {Button} from "@/core/components/ui/button";
import {TooltipProvider} from "@/core/components/ui/tooltip";
import {Program, SupportProgramMeta, SupportProgramPatient} from "@/program/interfaces/Program";
import {FollowUpEvent, ProgramTimelineEvent} from "@/program/interfaces/ProgramFollowUp";
import ProgramFollowUpService, {FOLLOW_UP_EVENT_LABELS} from "@/program/services/ProgramFollowUpService";
import {FOLLOW_UP_EVENT_STYLES, FOLLOW_UP_PLANNED_CLASSES} from "@/program/components/program-follow-up/followUpEventStyles";
import FollowUpPatientCell from "@/program/components/program-follow-up/FollowUpPatientCell.vue";
import FollowUpTimelineTrack from "@/program/components/program-follow-up/FollowUpTimelineTrack.vue";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import {useToastStore} from "@/stores/toast.store";

/** The number of patients per page, each page loading its events at once. */
const PAGE_SIZE = 25;

/** The axis window: months before and after today. */
const MONTHS_BEFORE = 6;
const MONTHS_AFTER = 2;

/** The months the axis moves by when panning. */
const PAN_MONTHS = 3;

/** The history depth, matching the year of sales the API serves. */
const HISTORY_MONTHS = 12;

const RECENCY_OPTIONS = [
    {months: 0, label: 'Tous'},
    {months: 1, label: '1 mois'},
    {months: 3, label: '3 mois'},
    {months: 6, label: '6 mois'},
    {months: 12, label: '1 an'},
];

const props = defineProps<{
    program: Program;
    /** The consumers, already filtered by the patient list. */
    consumers: SupportProgramPatient[];
    meta: SupportProgramMeta;
    /** The followed targeting groups labels, by slug. */
    groups: Record<string, string>;
}>();

const emit = defineEmits<{
    (e: 'open', consumer: SupportProgramPatient): void;
}>();

const router = useRouter();
const toastStore = useToastStore();

const state = reactive({
    recency: 0,
    page: 1,
    offset: 0,
    /** The events of the loaded consumers, by consumer key. */
    events: {} as Record<string, FollowUpEvent[]>,
    /** The number of sources received, out of the API and the HDS, by consumer key. */
    received: {} as Record<string, number>,
});

/** The events sources: the program sales from the API and the other events from the HDS. */
const SOURCES_COUNT = 2;

/** The keys being loaded, not to load them twice. */
const pending = new Set<string>();

const historyStart = dayjs().subtract(HISTORY_MONTHS, 'month').startOf('day');

const axis = computed(() => {
    const to = dayjs().add(MONTHS_AFTER, 'month').endOf('month').add(state.offset, 'month');

    return {from: to.subtract(MONTHS_BEFORE + MONTHS_AFTER, 'month').startOf('month').toDate(), to: to.toDate()};
});

const canGoBack = computed(() => dayjs(axis.value.from).isAfter(historyStart));

const position = (date: dayjs.Dayjs): number => {
    return (date.valueOf() - axis.value.from.getTime()) / (axis.value.to.getTime() - axis.value.from.getTime());
};

const todayPosition = computed(() => position(dayjs()));

const months = computed(() => {
    const result: { label: string, position: number }[] = [];

    for (let month = dayjs(axis.value.from); month.isBefore(axis.value.to); month = month.add(1, 'month')) {
        result.push({label: month.locale('fr').format('MMM YY'), position: position(month)});
    }

    return result;
});

/** Whether the program has validating groups (dispensing or act). */
const hasValidation = computed(() => Object.keys(props.meta.excluded ?? {}).length > 0);

/**
 * The consumers of the recency filter, by most recent program sale first: the
 * patients to follow up come before the ones gone for months.
 */
const sortedConsumers = computed(() => {
    const recencyStart = state.recency ? dayjs().subtract(state.recency, 'month') : null;
    const lastSale = (consumer: SupportProgramPatient) => Math.max(
        ...Object.values(consumer.last_visits ?? {}).map(date => (date ? new Date(date).getTime() : 0) || 0),
        0
    );

    return props.consumers
        .filter(consumer => !recencyStart
            || (!!consumer.last_visits?.excluded && dayjs(consumer.last_visits.excluded).isAfter(recencyStart)))
        .map(consumer => ({consumer, lastSale: lastSale(consumer)}))
        .sort((a, b) => b.lastSale - a.lastSale)
        .map(({consumer}) => consumer);
});

const pageCount = computed(() => Math.max(1, Math.ceil(sortedConsumers.value.length / PAGE_SIZE)));
const pageStart = computed(() => (state.page - 1) * PAGE_SIZE);
const pageConsumers = computed(() => sortedConsumers.value.slice(pageStart.value, pageStart.value + PAGE_SIZE));

/** The share of the current page sources received, from 0 to 1. */
const pageProgress = computed(() => {
    const keys = pageConsumers.value.map(consumer => consumer.key);
    const received = keys.reduce((sum, key) => sum + (state.received[key] ?? 0), 0);

    return keys.length ? received / (keys.length * SOURCES_COUNT) : 1;
});

/**
 * Load the events of the given consumers not loaded yet: their program sales
 * from the API and their other events from the HDS, in parallel. The events
 * are drawn once both sources answered, the progress moving at each one.
 */
const loadEvents = async (consumerKeys: string[]): Promise<void> => {
    const keys = consumerKeys.filter(key => !(key in state.events) && !pending.has(key));

    if (!keys.length) {
        return;
    }

    keys.forEach(key => pending.add(key));

    const salesByKey = new Map<string, SupportProgramPatient>();
    const timelineByKey = new Map<string, ProgramTimelineEvent[]>();
    let failed = false;

    const fail = () => {
        if (!failed) {
            failed = true;
            toastStore.showError({content: 'Une partie de l\'historique des patients n\'a pas pu être chargée.'});
        }
    };

    const receive = () => keys.forEach(key => state.received[key] = (state.received[key] ?? 0) + 1);

    await Promise.all([
        programApi().consumersSales(props.program.programmable_id, keys)
            .then(consumers => consumers.forEach(consumer => salesByKey.set(consumer.key, consumer)), fail)
            .then(receive),
        programApi().timeline(props.program.programmable_id, keys, historyStart.format('YYYY-MM-DD'))
            .then(timelines => timelines.forEach(timeline => timelineByKey.set(timeline.consumer_key, timeline.events)), fail)
            .then(receive),
    ]);

    keys.forEach(key => {
        // The events are drawn as is: no need to make them deeply reactive.
        state.events[key] = markRaw(ProgramFollowUpService.events(salesByKey.get(key), timelineByKey.get(key) ?? [], props.meta));
        pending.delete(key);
    });
};

/**
 * Load the current page events, then prefetch the next page ones.
 */
watch(pageConsumers, async consumers => {
    await loadEvents(consumers.map(consumer => consumer.key));

    const next = sortedConsumers.value.slice(pageStart.value + PAGE_SIZE, pageStart.value + 2 * PAGE_SIZE);
    loadEvents(next.map(consumer => consumer.key));
}, {immediate: true});

watch(() => [props.consumers, state.recency], () => state.page = 1);

const showConversation = (consumer: SupportProgramPatient) => {
    const consumerKey = ConsumerKeyService.defaultKey(consumer.key);
    const customerId = ConsumerKeyService.isBeneficiary(consumerKey) ? consumer.customer_id : parseInt(consumerKey.slice(2));

    router.push({name: 'customer.conversation', params: {customerId}});
};
</script>
